import {ReactElement, useCallback, useEffect, useMemo} from 'react';
import {AxiosError} from 'axios';
import {ClientAnalytics, Image} from '@shopify/hydrogen';
import {useQuery as useBackendQuery} from 'react-query';
import Select from 'react-select';
import {getFlavourNotes} from '../../backendApi/flavourNotesApi';
import {
  saveMyProductReview,
  SaveMyProductReviewPayloadType,
} from '../../backendApi/my-products';

import useFormState from '../../hooks/useFormState.client';
import {MemberProductType} from '../../types';

import SwalComponent from '../modals/SwalComponent.client';
import {PlaceholderWhiskyJPEG} from '../../assets/img';
import {useMemberProduct} from '../../contexts/MemberProductProvider.client';
import {
  RadioProductScoreField,
  ProductScoreType,
} from '../formControls/RadioProductScoreField.client';
import {TwlAnalyticsEvent} from '../../constants';

interface ProductReviewFormProps {
  shopifyProductId?: string;
  productTitle?: string;
  productImageUrl?: string;
  onSuccess?: (memberProduct: MemberProductType) => void;
  onError?: (error: AxiosError) => void;
}

export default function ProductReviewForm({
  shopifyProductId,
  productTitle,
  productImageUrl,
  onSuccess,
  onError,
}: ProductReviewFormProps): ReactElement {
  const {data: flavourNotesData} = useBackendQuery(
    'flavourNotes',
    () => getFlavourNotes(),
    {retry: false},
  );

  const selectNotesOptions = useMemo(() => {
    if (!flavourNotesData) return [];

    return flavourNotesData.map(({name}) => ({
      value: name,
      label: name,
    }));
  }, [flavourNotesData]);

  const {myMemberProduct, setMyMemberProduct} = useMemberProduct();

  const handleSuccess = useCallback(
    (memberProduct: MemberProductType) => {
      onSuccess?.(memberProduct);
      if (memberProduct.rating !== myMemberProduct?.rating) {
        ClientAnalytics.publish(TwlAnalyticsEvent.RATE_PRODUCT, true, {
          ...ClientAnalytics.getPageAnalyticsData(),
          newRating: memberProduct.rating,
          oldRating: myMemberProduct?.rating,
          productId: memberProduct.product?.uuid,
        });
      }
      setMyMemberProduct(memberProduct);
      SwalComponent.fire({
        title: 'Thanks for your review!',
        icon: 'success',
      });
    },
    [myMemberProduct?.rating, onSuccess, setMyMemberProduct],
  );

  const {formFields, setFormFields, handleChange, handleSubmit} = useFormState<
    SaveMyProductReviewPayloadType,
    MemberProductType
  >(
    saveMyProductReview,
    {
      product: shopifyProductId,
      rating: null,
      review_title: '',
      review_description: '',
      aroma_enjoyment_score: null,
      balance_and_complexity_score: null,
      body_and_mouthfeel_score: null,
      finish_length_score: null,
      taste_enjoyment_score: null,
      aroma_notes: [],
      taste_notes: [],
      finish_notes: [],
    },
    {
      onError,
      onSuccess: handleSuccess,
    },
  );

  useEffect(() => {
    if (myMemberProduct) {
      setFormFields((prevState) => ({
        ...prevState,
        ...Object.fromEntries(
          Object.keys(prevState).map((key) => [key, myMemberProduct[key]]),
        ),
        product: prevState.product,
      }));
    }
  }, [myMemberProduct, setFormFields]);

  return (
    <div className="row mt-25">
      <div className="col-lg-3">
        <div className="modal-add-review-image">
          {
            <Image
              src={productImageUrl ?? PlaceholderWhiskyJPEG}
              width={250}
              height={250}
              alt={productTitle || 'Whisky'}
            />
          }
        </div>
      </div>
      <div className="col-lg-9">
        <form onSubmit={handleSubmit}>
          <h3 className="title-2 mt-10">
            Write a review for <strong>{productTitle}</strong>
          </h3>
          <div className="separator mt-20 mb-25"></div>
          <div className="secondary-font">Rating</div>
          <RadioProductScoreField
            key="reviewModalRating"
            scoreType={ProductScoreType.rating}
            name="rating"
            checkedValue={formFields.rating}
            onChange={handleChange}
          />
          <div className="row">
            <label className="col-sm-12 mt-30">
              <span className="secondary-font">Review Title</span>
              <input
                type="text"
                name="review_title"
                className="form__element"
                value={formFields.review_title}
                onChange={handleChange}
                placeholder="Summarise the whisky or highlight an interesting detail"
                required
              />
            </label>
            <label className="col-sm-12 mt-30">
              <span className="secondary-font">Review Description</span>
              <textarea
                className="form__element"
                name="review_description"
                value={formFields.review_description}
                placeholder="Tell us what you think about this whisky"
                onChange={handleChange}
              ></textarea>
            </label>
            <div className="col-lg-4 col-md-6">
              <RadioProductScoreField
                name="aroma_enjoyment_score"
                onChange={handleChange}
                checkedValue={formFields.aroma_enjoyment_score}
              >
                <legend className="secondary-font">
                  Aroma Enjoyment:
                  <span className="modal-rating-score-item__help tooltip tooltip-trigger">
                    <span className="tooltiptext">
                      A score of the overall enjoyment of smelling a whisky. The
                      longer you want to sit and sniff, the more you simply
                      enjoy the aromas, the higher the value.
                    </span>
                  </span>
                </legend>
              </RadioProductScoreField>
            </div>
            <div className="col-lg-4 col-md-6">
              <RadioProductScoreField
                name="taste_enjoyment_score"
                onChange={handleChange}
                checkedValue={formFields.taste_enjoyment_score}
              >
                <legend className="secondary-font">
                  Taste Enjoyment:
                  <span className="modal-rating-score-item__help tooltip tooltip-trigger">
                    <span className="tooltiptext">
                      This is about the overall enjoyment of the flavours.
                      It&apos;s the simple measurement of how nice the whisky
                      tastes and how much you enjoy the flavours.
                    </span>
                  </span>
                </legend>
              </RadioProductScoreField>
            </div>
            <div className="col-lg-4 col-md-6">
              <RadioProductScoreField
                name="body_and_mouthfeel_score"
                onChange={handleChange}
                checkedValue={formFields.body_and_mouthfeel_score}
              >
                <legend className="secondary-font">
                  Body &amp; Mouthfeel:
                  <span className="modal-rating-score-item__help tooltip tooltip-trigger">
                    <span className="tooltiptext">
                      A textural evaluation of a whisky, describing the weight
                      of a whisky in the mouth and how will the viscosity of a
                      whisky holds throughout the palate. Thin feeling, light
                      weighing, watery whisky would receive lower points while
                      thick feeling, heavy weighing viscous whisky would receive
                      higher points. A whisky that gives way to too much dryness
                      or tannin would receive lower points, while one holding a
                      solid, balanced structure of viscosity and/or tannin would
                      receive higher points.
                    </span>
                  </span>
                </legend>
              </RadioProductScoreField>
            </div>
            <div className="col-lg-4 col-md-6">
              <RadioProductScoreField
                name="balance_and_complexity_score"
                onChange={handleChange}
                checkedValue={formFields.balance_and_complexity_score}
              >
                <legend className="secondary-font">
                  Balance &amp; Complexity:
                  <span className="modal-rating-score-item__help tooltip tooltip-trigger">
                    <span className="tooltiptext">
                      Measures the variety of flavours across nose, palate and
                      finish and how well the flavours work together; from
                      spirit, time and oak. A simple whisky, one with fewer
                      distinguishable flavours would receive lower points, while
                      a more complex whisky with a wide variety of flavour
                      working together would receive a higher score.
                    </span>
                  </span>
                </legend>
              </RadioProductScoreField>
            </div>
            <div className="col-lg-4 col-md-6">
              <RadioProductScoreField
                name="finish_length_score"
                onChange={handleChange}
                checkedValue={formFields.finish_length_score}
              >
                <legend className="secondary-font">
                  Finish/Length
                  <span className="modal-rating-score-item__help tooltip tooltip-trigger">
                    <span className="tooltiptext">
                      Part of the palate evaluation of a whisky, length
                      describes how long the flavour of a whisky continuous to
                      deliver through the palate and finish. Whiskies where the
                      flavour dissipates quickly would receive lower points,
                      while a longer, lingering flavour would receive more
                      points.
                    </span>
                  </span>
                </legend>
              </RadioProductScoreField>
            </div>
          </div>
          <div className="row mt-25">
            <h6 className="title-4">Tasting notes -</h6>
            <div className="col-sm-12 mt-30">
              <span className="secondary-font">Aroma</span>
              <Select
                className="ts-wrapper form__multi_select mt-10"
                classNamePrefix="ts"
                name="aroma_notes"
                placeholder="Start typing..."
                options={selectNotesOptions}
                value={formFields.aroma_notes.map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(newValue) => {
                  setFormFields((prevState) => ({
                    ...prevState,
                    aroma_notes: newValue.map(({value}) => value),
                  }));
                }}
                isMulti
              />
            </div>
            <div className="col-sm-12 mt-30">
              <span className="secondary-font">Taste</span>
              <Select
                className="ts-wrapper form__multi_select mt-10"
                classNamePrefix="ts"
                name="taste_notes"
                placeholder="Start typing..."
                options={selectNotesOptions}
                value={formFields.taste_notes.map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(newValue) => {
                  setFormFields((prevState) => ({
                    ...prevState,
                    taste_notes: newValue.map(({value}) => value),
                  }));
                }}
                isMulti
              />
            </div>
            <div className="col-sm-12 mt-30">
              <span className="secondary-font">Finish</span>
              <Select
                className="ts-wrapper form__multi_select mt-10"
                classNamePrefix="ts"
                name="finish_notes"
                placeholder="Start typing..."
                options={selectNotesOptions}
                value={formFields.finish_notes.map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(newValue) => {
                  setFormFields((prevState) => ({
                    ...prevState,
                    finish_notes: newValue.map(({value}) => value),
                  }));
                }}
                isMulti
              />
            </div>
            <div className="col-sm-12 mt-30">
              <button className="button button_full">Submit Review</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
